import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Activity,
  Atom,
  Bot,
  Boxes,
  Braces,
  Brain,
  CalendarDays,
  Cloud,
  CreditCard,
  Database,
  Dumbbell,
  FlaskConical,
  Landmark,
  Layers,
  Mail,
  MapPin,
  MessageCircle,
  Send,
  Server,
  ShoppingCart,
  Sparkles,
  Truck,
  Waves,
  Workflow,
  ClipboardList,
} from "lucide";
import { ease } from "./tokens";
import { STATIC_MODE, useReveal } from "./primitives";

/* Lucide icon data: array of [tagName, attributes] tuples. */
type IconNode = [string, Record<string, string | number>][];

const ICON_DATA = {
  activity: Activity,
  atom: Atom,
  bot: Bot,
  boxes: Boxes,
  braces: Braces,
  brain: Brain,
  calendar: CalendarDays,
  cart: ShoppingCart,
  chat: MessageCircle,
  clipboard: ClipboardList,
  cloud: Cloud,
  "credit-card": CreditCard,
  database: Database,
  dumbbell: Dumbbell,
  flask: FlaskConical,
  landmark: Landmark,
  layers: Layers,
  mail: Mail,
  "map-pin": MapPin,
  send: Send,
  server: Server,
  sparkles: Sparkles,
  truck: Truck,
  waves: Waves,
  workflow: Workflow,
} as unknown as Record<string, IconNode>;

export type IconName = keyof typeof ICON_DATA;

type Props = {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  delay?: number;
  color?: string;
  /** When provided, drives the draw state instead of viewport entry. */
  active?: boolean;
};

const shapeComponents: Record<string, any> = {
  path: motion.path,
  circle: motion.circle,
  rect: motion.rect,
  line: motion.line,
  polyline: motion.polyline,
  polygon: motion.polygon,
  ellipse: motion.ellipse,
};

/**
 * Animated stroke-drawn icon (lucide outlines + framer-motion pathLength).
 * Draws itself once when it enters the viewport; wiggles on hover.
 * Static/reduced-motion render fully drawn.
 */
const AnimatedIcon: React.FC<Props> = ({
  name,
  size = 18,
  strokeWidth = 2,
  className,
  delay = 0,
  color = "currentColor",
  active,
}) => {
  const reduced = useReducedMotion();
  const { ref, shown: inView } = useReveal<SVGSVGElement>();
  const nodes = ICON_DATA[name];
  const instant = STATIC_MODE || reduced;
  const shown = active !== undefined ? active : inView;

  return (
    <motion.svg
      ref={ref}
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      whileHover={instant ? undefined : { rotate: -10, scale: 1.12 }}
      transition={{ duration: 0.4, ease: ease.out }}
      style={{ display: "inline-block", verticalAlign: "middle" }}
    >
      {nodes.map(([tag, attrs], i) => {
        const Shape = shapeComponents[tag] ?? motion.path;
        const { key: _unused, ...rest } = attrs as any;
        return (
          <Shape
            key={i}
            {...rest}
            initial={false}
            animate={{
              pathLength: instant || shown ? 1 : 0,
              opacity: instant || shown ? 1 : 0,
            }}
            transition={{
              pathLength: {
                duration: 0.7,
                delay: delay + i * 0.08,
                ease: "easeOut",
              },
              opacity: { duration: 0.01, delay: delay + i * 0.08 },
            }}
          />
        );
      })}
    </motion.svg>
  );
};

export default AnimatedIcon;
